import React from "react";
import Select from "react-select";

import { Button, ButtonGroup } from "aible";

import numeral from "numeral";

import { LineInput } from "components/Inputs/LineInput";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

import PieChart from "components/Project/PieChart";
import TinyBarChart from "components/Project/TinyBarChart";
import PredictionDriverToolTip from "components/Project/PredictionDriverToolTip";

import RunPlotContainer from "containers/RunPlot/RunPlot";
import RunFrontierContainer from "containers/FrontierSliders/RunFrontier";

import { BUSINESS_OBJECTIVE, BIAS_REDUCTION, isSameSetup, FAIRNESS_OBJECTIVE } from "containers/Project/reducer";

import { defaultSetup } from "containers/Setup/reducer";

import ProjectHelp from "components/UnifiedProject/Help/ProjectHelp";
import ProjectHelpItem from "components/UnifiedProject/Help/ProjectHelpItem";
import SplitChart from "components/UnifiedProject/Objective/SplitChart";

import PurpleFlagIcon from "images/PurpleFlagIcon.svg";

import LeafIcon from "images/G/Leaf.svg";
import UpGraphIcon from "images/G/UpGraph.svg";
import DownGraphIcon from "images/G/DownGraph.svg";
import WorldIcon from "images/G/World.svg";
import BalanceIcon from "images/G/Balance.svg";
import NoneIcon from "images/G/None.svg";
import SettingsIcon from "images/G/Settings.svg";

import LeafIconP from "images/G/LeafP.svg";
import UpGraphIconP from "images/G/UpGraphP.svg";
import DownGraphIconP from "images/G/DownGraphP.svg";
import WorldIconP from "images/G/WorldP.svg";
import BalanceIconP from "images/G/BalanceP.svg";
import NoneIconP from "images/G/NoneP.svg";
import SettingsIconP from "images/G/SettingsP.svg";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";

import styles from "./style.module.css";

const DEFAULT_TOLERANCE_BOUND = defaultSetup.fairnessToleranceBound;

export default class Objective extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isHammer: false,
      isHelpClosed: false,
      isDrivers: true,
      needsRecalculate: false
    };
  }

  getIsOverallSegment() {
    const { project, optimized } = this.props;

    return (
      optimized &&
      optimized.innerSegment &&
      optimized.innerSegment.conditions.length === 0 &&
      optimized.innerSegment.job &&
      optimized.innerSegment.job.job_id === optimized.innerId &&
      project &&
      project.segments &&
      project.segments.length > 1
    );
  }

  renderProgress(label) {
    const { optimized } = this.props;

    return (
      <div className={styles.eLight}>
        <div className={styles.lFlex}>
          <div className={styles.eCircle} />
          {optimized && optimized.isCreating ? "Finding Optimal " + label : "Optimization Not Started"}
        </div>
        <div className={styles.eLightBar} />
      </div>
    );
  }

  renderMix() {
    const { project, createSplits, optimized, optimizer, modelGroups } = this.props;
    const { isCreating } = this.state;

    const splitOptions =
      optimized && optimized.dataset
        ? Object.keys(optimized.dataset.fields)
            .filter((f) => optimized.dataset.fields[f].type === "categorical" && f !== optimized.dataset.outcome)
            .filter((f) =>
              optimized && optimized.setup && optimized.setup.ignored && optimized.setup.ignored[f] === true
                ? false
                : true
            )
            .map((f) => ({
              label: optimized.dataset.fields[f].label,

              value: optimized.dataset.fields[f].label
            }))
        : [];

    const drilldownValues = (modelGroups && modelGroups.drilldownValues && modelGroups.drilldownValues) || [];

    let drilldownSlice = undefined;

    for (let value of drilldownValues) {
      if (
        value.value != "Overall" &&
        (optimizer.drilldownValue == "Overall" || value.value === optimizer.drilldownValue)
      ) {
        drilldownSlice = value;
        break;
      }
    }

    const otherSlices = drilldownSlice
      ? drilldownValues.filter((v) => v.column && v.column == drilldownSlice.column && v.column !== "Overall")
      : undefined;

    let loadingSplits = false;

    otherSlices &&
      otherSlices.forEach((v) => {
        if (isNaN(v.optimalPct)) {
          loadingSplits = true;
        }
      });

    const darkSelectStyle = {
      option: (provided, state) => ({
        ...provided,
        background: state.isFocused ? "#512f77" : "#362e30",
        color: "white !important"
      }),
      control: (provided, state) => ({
        ...provided,
        background: "#362e44",
        border: "1px solid #828282",
        color: "white !important"
        // none of react-select's styles are passed to <Control />
      }),
      singleValue: (provided, state) => ({
        ...provided,
        color: "white"
      }),
      menuList: (provided, state) => ({
        ...provided,
        paddingTop: "0px",
        paddingBottom: "0px"
      })
    };

    return (
      <div className={styles.mixHolder} style={{ overflow: "visible" }}>
        {project && project.segments && project.segments.length === 1 && isCreating !== true ? (
          <div className={styles.splitFieldHolder}>
            <div className={styles.oItemBotHeader} style={{ textAlign: "center" }}>
              {"Split on Field"}
            </div>
            <div className={styles.splitField}>
              <Select options={splitOptions} ref="splitOne" styles={darkSelectStyle} />
            </div>
            <Button
              onClick={() => {
                this.setState({ isCreating: true });
                setTimeout(() => this.setState({ isCreating: false }), 5000);
                createSplits(
                  this.refs["splitOne"].state.value !== null ? this.refs["splitOne"].state.value.value : undefined
                );
              }}
              style={{ whiteSpace: "nowrap" }}
              isDark
            >
              {"Create Subgroups"}
            </Button>
          </div>
        ) : loadingSplits || isCreating ? (
          <div className={styles.smallLoader} style={{ padding: "2vw" }}>
            <LoadingIndicator style={{ marginTop: "-20px" }} color={"#868595"} />
            <div style={{ fontWeight: "300", marginTop: "-10px" }}>{"Training AI Models"}</div>
          </div>
        ) : (
          <div>
            <div className={styles.pieHolder}>
              <PieChart
                secondaryColors
                pData={
                  otherSlices
                    ? otherSlices.map((s) => ({
                        name: s.value,
                        runId: s.runId,
                        value: Math.round(s.optimalPct),
                        color: s.color
                      }))
                    : []
                }
                onClick={(runId, name) => updateDrilldownValue(runId, name)}
                unit={optimized.unitLabelShort}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  renderIsContinuous() {
    const { id, setup, optimized, project, modelGroups, runId, model, isEditBlueprint } = this.props;

    const { isDrivers } = this.state;

    const underPredictLabel = setup.underPredictAction ? setup.underPredictAction : "Underestimate Cost";

    const overPredictLabel = setup.overPredictAction ? setup.overPredictAction : "Overestimate Cost";

    const isOverallSegment = this.getIsOverallSegment();

    return (
      <div
        className={styles.emptyOptimize}
        style={optimized && optimized.isCreating === false ? { paddingBottom: "200px" } : {}}
      >
        <div className={styles.emptyTop}>
          <ProjectHelp
            title={
              isEditBlueprint
                ? "Editing Blueprint: Business Cost Benefits and Labels"
                : optimized
                  ? "Optimize Business Impact by Adjusting Business Assumptions"
                  : "Add Baseline Business Assumptions to Optimize Business Impact"
            }
            isDark
            onToggle={(isClosed) => this.setState({ isHelpClosed: isClosed })}
            noBot
          >
            {isEditBlueprint !== true ? (
              <React.Fragment>
                <ProjectHelpItem isDark type={"targetGrey"}>
                  {"After Training the AI Aible will show the recommended resourcing, " +
                    "mix, and revenue that optimizes your profit and compares it to the baseline."}
                </ProjectHelpItem>
                <ProjectHelpItem isDark type={"rocketGrey"}>
                  {"You will be able to keep changing these assumptions even after training the AI."}
                </ProjectHelpItem>
              </React.Fragment>
            ) : (
              ""
            )}
          </ProjectHelp>
          <div className={styles.eBoxHolder}>
            {optimized && optimized.isCreating === false ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Optimized " + underPredictLabel}</div>
                <div className={styles.eSubDark}>
                  {"This is the amount of the underestimate times" + " the total number of underestimated houses."}
                </div>
                <div className={styles.eLightLarge}>{numeral(optimized.underEstimate).format("0.0a")}</div>
              </div>
            ) : optimized && optimized.isCreating ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Optimizing " + underPredictLabel}</div>
                <div className={styles.eLoading}>
                  <LoadingIndicator style={{ margin: "0px" }} color={"#868595"} />
                </div>
                {this.renderProgress(underPredictLabel)}
              </div>
            ) : (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Baseline " + underPredictLabel}</div>
                <div className={styles.eSubDark}>
                  {"This is your Business Assumptions Costs (the total cost " +
                    "per opportunity) times the Total Opportunities " +
                    "you expect to pursue."}
                </div>
                <div className={styles.eDarkLarge}>{""}</div>
              </div>
            )}
            {optimized && optimized.isCreating === false ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Optimized " + overPredictLabel}</div>
                <div className={styles.eSubDark}>
                  {"This is the amount of the overestimate times the " + "total number of overestimated houses."}
                </div>
                <div className={styles.eLightLarge}>{numeral(optimized.overEstimate).format("0.0a")}</div>
              </div>
            ) : optimized && optimized.isCreating ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Optimizing " + overPredictLabel}</div>
                <div className={styles.eLoading}>
                  <LoadingIndicator style={{ margin: "0px" }} color={"#868595"} />
                </div>
                {this.renderProgress(overPredictLabel)}
              </div>
            ) : (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Baseline " + overPredictLabel}</div>
                <div className={styles.eSubDark}>
                  {"This uses your Business Assumption Benefits (expected revenue " +
                    "per opportunitiy) times your Total Expected Win Count."}
                </div>
                <div className={styles.eDarkLarge}>{""}</div>
              </div>
            )}
            {optimized && optimized.isCreating === false ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Optimized Error Cost"}</div>
                <div className={styles.eSubDark}>
                  {"This is the sum of your total underestimate and overestimate costs."}
                </div>
                <div className={styles.eLightLarge}>
                  {numeral(optimized.overEstimate + optimized.underEstimate).format("0.0a")}
                </div>
              </div>
            ) : optimized && optimized.isCreating ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Error Cost"}</div>
                <div className={styles.eLoading}>
                  <LoadingIndicator style={{ margin: "0px" }} color={"#868595"} />
                </div>
                {this.renderProgress("Error Cost")}
              </div>
            ) : (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Baseline Error Cost"}</div>
                <div className={styles.eSubDark}>
                  {"This is the sum of the unique businesss circumstances you defined" +
                    " in the Costs/Benefits and Expected Win Count across all" +
                    " potential opportunities."}
                </div>
                <div className={styles.eDarkLarge}>{""}</div>
              </div>
            )}
          </div>
        </div>
        {false && isEditBlueprint ? (
          this.renderEditBlueprintBot()
        ) : (
          <div className={styles.emptyBot}>
            <div className={styles.eBotHeader}>
              <div className={styles.eBotHeaderItem} style={{ minWidth: "50%" }}>
                {"Business Assumptions"}
              </div>
              <div
                className={styles.eBotHeaderItem}
                style={{
                  minWidth: "calc(50% - 100px)",
                  marginRight: "20px"
                }}
              >
                {"Optimal AI Model"}
                {isOverallSegment !== true && id !== "new" && (
                  <ButtonGroup>
                    <Button
                      onClick={() =>
                        this.setState({
                          isDrivers: true
                        })
                      }
                      active={this.state.isDrivers === true}
                    >
                      {"Drivers"}
                    </Button>
                    <Button
                      onClick={() =>
                        this.setState({
                          isDrivers: false
                        })
                      }
                      active={this.state.isDrivers === false}
                    >
                      {"Charts"}
                    </Button>
                  </ButtonGroup>
                )}
              </div>
            </div>
            <div className={styles.eBotBody}>
              <div className={styles.eBotItem} style={{ minWidth: "50%" }}>
                {/* {this.renderAssumptions()} */}
              </div>
              <div
                className={styles.eBotItem}
                style={{
                  minWidth: "calc(50% - 100px)",
                  marginRight: "20px"
                }}
              >
                {optimized && optimized.isCreating === false ? (
                  isOverallSegment || isDrivers ? (
                    <div>
                      <div className={styles.aHeader} style={{ marginBottom: "10px" }}>
                        {"Key Drivers"}
                        <PredictionDriverToolTip />
                      </div>
                      <div className={styles.nItemBotContent} style={{ overflow: "auto" }}>
                        {isOverallSegment
                          ? modelGroups.topDrivers.filter((s, i) => i > 0).map((s) => this.renderDrivers(s, 4, false))
                          : this.renderDrivers(
                              (modelGroups.topDrivers || []).find((driver) => driver.runId === optimized.innerId),
                              5,
                              true
                            )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className={styles.mBold}>
                        <span className={styles.spanLabel}>{"Optimal Model:"}</span>
                        <span>{model ? model.label : "..."}</span>
                      </div>
                      ""
                      <div className={styles.mRunFrontier}>
                        <RunFrontierContainer id={runId} projectId={id} />
                      </div>
                      <div
                        className={styles.runPlot}
                        style={{
                          height: this.state.isHelpClosed ? "37vh" : "30vh"
                        }}
                      >
                        {
                          <RunPlotContainer
                            id={runId}
                            isMiniOptimize
                            projectId={project ? project.id : undefined}
                            forceEnableBts
                            hideFrontier
                          />
                        }
                      </div>
                    </div>
                  )
                ) : (
                  <div className={styles.eBotBox}>
                    <img src={PurpleFlagIcon} className={styles.eIcon} />
                    <div className={styles.eBotBoxBold}>
                      {"Once the AI has been trained this section will " +
                        "display the model that best fits your use case."}
                    </div>
                    <div>
                      {"The optimal model will be based on the cost, benefit, and" +
                        " data assumptions you defined in the first two sections."}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  getBaselines() {
    const { setup } = this.props;

    const truePositive =
      setup.isRealityHammer && setup.baselineCounts
        ? Number(setup.baselineCounts.truePositive)
        : Number(setup.truePositive);
    const falsePositive =
      setup.isRealityHammer && setup.baselineCounts
        ? Number(setup.baselineCounts.falsePositive)
        : Number(setup.falsePositive);
    const trueNegative =
      setup.isRealityHammer && setup.baselineCounts
        ? Number(setup.baselineCounts.trueNegative)
        : Number(setup.trueNegative);
    const falseNegative =
      setup.isRealityHammer && setup.baselineCounts
        ? Number(setup.baselineCounts.falseNegative)
        : Number(setup.falseNegative);

    const pursued = truePositive + falsePositive;
    const notPursued = trueNegative + falseNegative;

    const baselineTotal = pursued + notPursued;

    const bPositives = truePositive + falseNegative;
    const bNegatives = falsePositive + trueNegative;

    const baselineTotalWinRate = ((bPositives / (bPositives + bNegatives)) * 100).toFixed(2);

    return {
      baselineWinRate: baselineTotalWinRate,
      baselineTotal: baselineTotal
    };
  }

  renderObjectives() {
    const {
      runId,
      optimized,
      modelGroups,
      setup,
      updateFairnessObjective,
      updateBiasReduction,
      updateTargetPositiveAllocation,
      isOptimizerWithinBounds,
      updateFairnessToleranceBound,
      recalculateBudget
    } = this.props;

    const rSetup = optimized && optimized.setup ? optimized.setup : setup ? setup : {};

    if (!rSetup.splitColumn) {
      return (
        <div style={{ marginTop: "20px" }}>Please select a field to split on to specify fairness constraints.</div>
      );
    } else if (!this.getIsOverallSegment()) {
      return (
        <div style={{ marginTop: "20px" }}>Please select the overall segment to specify fairness constraints.</div>
      );
    }

    const splitChartOptions = [
      {
        key: "units",
        value: "units",
        label: `Percentage of Total ${rSetup.transactionLabel} to ${rSetup.trueAction}`
      },
      {
        key: "cost",
        value: "cost",
        label: `Percentage of Total ${optimized.resourcingLabel}`
      },
      {
        key: "profit",
        value: "profit",
        label: `Percentage of Total ${optimized.profitLabel}`
      },
      {
        key: "accuracy",
        value: "accuracy",
        label: "Accuracy"
      },
      {
        key: "true_positive_rate",
        value: "true_positive_rate",
        label: `${rSetup.truePositiveLabel} Rate`
      },
      {
        key: "false_positive_rate",
        value: "false_positive_rate",
        label: `${rSetup.falsePositiveLabel} Rate`
      },
      {
        value: undefined,
        label: "(Reset to Default)"
      }
    ];

    let splitOptionValue = this.state.splitOption;

    let splitOptionKey = undefined;

    if (splitOptionValue === undefined || splitOptionValue.value === undefined) {
      switch (rSetup.fairnessObjective) {
        case FAIRNESS_OBJECTIVE.ACCURACY:
          splitOptionKey = "accuracy";
          break;
        case FAIRNESS_OBJECTIVE.ODDS:
        case FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE:
          splitOptionKey = "true_positive_rate";
          break;
        case FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE:
          splitOptionKey = "false_positive_rate";
          break;
        case FAIRNESS_OBJECTIVE.ALLOCATION:
          switch (rSetup.biasReduction) {
            case BIAS_REDUCTION.COUNT:
              splitOptionKey = "units";
              break;
            case BIAS_REDUCTION.EFFORT:
              splitOptionKey = "cost";
              break;
            case BIAS_REDUCTION.IMPACT:
              splitOptionKey = "profit";
              break;
          }
      }

      if (splitOptionKey !== undefined) {
        splitOptionValue = splitChartOptions.find((x) => x.key == splitOptionKey);
      }
    }

    if (splitOptionKey === undefined && splitOptionValue !== undefined) {
      splitOptionKey = splitOptionValue.key;
    }

    const subgroups = ((modelGroups || {}).drilldownValues || []).filter((d) => d.value !== "Overall");

    if (subgroups && subgroups.length > 0 && !subgroups[0].ccm) {
      return (
        <div className={styles.objective} style={{ marginTop: "10px" }}>
          <div className={styles.smallLoader} style={{ padding: "2vw" }}>
            <LoadingIndicator style={{ marginTop: "-20px" }} color={"#868595"} />
            <div
              style={{
                fontWeight: "300",
                marginTop: "-10px",
                textAlign: "center"
              }}
            >
              {"Training AI Models"}
            </div>
          </div>
        </div>
      );
    }

    const allocationTotal = subgroups
      .map((d) =>
        d.setup.targetPositiveAllocation !== undefined ? d.setup.targetPositiveAllocation : d.pctOfRows * 100
      )
      .map(Number)
      .reduce((x, y) => x + y, 0);

    const splitChartData = {};

    for (let option of splitChartOptions) {
      splitChartData[option.key] = subgroups.map((s) => {
        let value = undefined;

        switch (option.key) {
          case "profit":
          case "cost":
          case "units":
            value = s[option.key] / subgroups.map((s) => s[option.key]).reduce((x, y) => x + y, 0);
            break;

          case "accuracy":
            value = s.ccm?.getAccuracy();
            break;

          case "true_positive_rate":
            value = s.ccm?.getTruePositiveRate();
            break;

          case "false_positive_rate":
            value = s.ccm?.getFalsePositiveRate();
            break;
        }

        value = (value * 100).toFixed(2);

        return {
          Subgroup: s.label,
          [option.label]: value
        };
      });
    }

    const values = subgroups
      .map(
        (d) =>
          (splitChartData[splitOptionKey].find((x) => x.Subgroup == d.label) || {})[
            (splitChartOptions.find((x) => x.key == splitOptionKey) || {}).label
          ]
      )
      .map(Number);

    const disclaimer = (
      <div className={styles.resultsWarning}>
        <div className={styles.oTitle} style={{ fontSize: "14px" }}>
          <FontAwesomeIcon icon={faExclamationCircle} /> Actual Results May Vary
        </div>
        <div style={{ marginTop: "5px" }}>
          These results are based on out of sample validation data. Your actual real-world results may vary, but the
          performance will be monitored.
        </div>
      </div>
    );

    return (
      <div className={styles.objective}>
        <div className={styles.bRow} style={{ flexDirection: "column" }}>
          <div className={styles.bObjective}>
            <div className={styles.oTitle}>{"I would like to ensure that all subgroups have:"}</div>
            <div className={styles.oActions}>
              <ButtonGroup>
                <Button
                  className={styles.oButton}
                  active={
                    rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.NONE || rSetup.fairnessObjective === undefined
                  }
                  onClick={() => updateFairnessObjective(runId, FAIRNESS_OBJECTIVE.NONE)}
                >
                  <div className={styles.innerButton}>
                    <img
                      src={
                        rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.NONE || rSetup.fairnessObjective === undefined
                          ? NoneIconP
                          : NoneIcon
                      }
                    />
                    {"No Equality Constraints"}
                  </div>
                </Button>
                <Button
                  className={styles.oButton}
                  active={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.ALLOCATION}
                  onClick={() => updateFairnessObjective(runId, FAIRNESS_OBJECTIVE.ALLOCATION)}
                >
                  <div className={styles.innerButton}>
                    <img
                      src={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.ALLOCATION ? BalanceIconP : BalanceIcon}
                    />
                    {"Representative Share"}
                  </div>
                </Button>
                <Button
                  className={styles.oButton}
                  active={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE}
                  onClick={() => updateFairnessObjective(runId, FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE)}
                >
                  <div className={styles.innerButton}>
                    <img
                      src={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE ? WorldIconP : WorldIcon}
                    />
                    {`Equal ${rSetup.truePositiveLabel} Rate`}
                  </div>
                </Button>
                <Button
                  className={styles.oButton}
                  active={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE}
                  onClick={() => updateFairnessObjective(runId, FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE)}
                >
                  <div className={styles.innerButton}>
                    <img
                      src={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE ? WorldIconP : WorldIcon}
                    />
                    {`Equal ${rSetup.falsePositiveLabel} Rate`}
                  </div>
                </Button>
                <Button
                  className={styles.oButton}
                  active={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.ODDS}
                  onClick={() => updateFairnessObjective(runId, FAIRNESS_OBJECTIVE.ODDS)}
                >
                  <div className={styles.innerButton}>
                    <img src={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.ODDS ? WorldIconP : WorldIcon} />
                    {`Equal Odds`}
                  </div>
                </Button>
                <Button
                  className={styles.oButton}
                  active={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.ACCURACY}
                  onClick={() => updateFairnessObjective(runId, FAIRNESS_OBJECTIVE.ACCURACY)}
                >
                  <div className={styles.innerButton}>
                    <img src={rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.ACCURACY ? WorldIconP : WorldIcon} />
                    {"Equal Accuracy"}
                  </div>
                </Button>
              </ButtonGroup>
            </div>
          </div>
          <div className={styles.fairnessDescription}>
            {(() => {
              switch (rSetup.fairnessObjective) {
                case FAIRNESS_OBJECTIVE.ALLOCATION:
                  return "Representative Share, also referred to as Demographic Parity, will attempt to assign a fair share to each of the subgroups. By default a fair share is determined by the fraction of rows in each subgroup relative to the total number of rows. This share can also be customized to match your requirements.";
                case FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE:
                  return `Equal ${rSetup.truePositiveLabel} Rate, also referred to as Equal Opportunity or True Positive Rate Parity, will attempt to balance each subgroup so that the true positive rate is roughly the same across all subgroups.`;
                case FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE:
                  return `Equal ${rSetup.falsePositiveLabel} Rate, also referred to as False Positive Rate Parity, will attempt to balance each subgroup so that the false positive rate is roughly the same across all subgroups.`;
                case FAIRNESS_OBJECTIVE.ODDS:
                  return `Equal Odds will attempt to balance each subgroup so that the true positive rate and the false positive rate are roughly the same across all subgroups.`;
                case FAIRNESS_OBJECTIVE.ACCURACY:
                  return `Equal Accuracy will attempt to balance each subgroup so that accuracy or conversely the error rate is roughly the same across all subgroups.`;
                default:
                  return "";
              }
            })()}
          </div>
          {optimized && optimized.bestModel && isOptimizerWithinBounds !== true && (
            <div className={styles.bObjective}>
              <div className={styles.errors} style={{ marginTop: "10px" }}>
                {"The Aible Optimizer could not find a solution"}
              </div>
            </div>
          )}
        </div>
        {rSetup &&
          rSetup.splitColumn &&
          rSetup.fairnessObjective !== FAIRNESS_OBJECTIVE.NONE &&
          rSetup.fairnessObjective !== undefined && (
            <div className={styles.bRow} style={{ borderBottom: "none" }}>
              {rSetup.fairnessObjective === FAIRNESS_OBJECTIVE.ALLOCATION && (
                <div style={{ marginLeft: "20px" }} className={styles.bObjective}>
                  <div className={styles.oTitle}>{"Each subgroup should make up a representative share of:"}</div>
                  <div className={styles.oActions}>
                    <ButtonGroup>
                      <Button
                        className={styles.oButton}
                        active={rSetup.biasReduction === BIAS_REDUCTION.COUNT}
                        onClick={() => updateBiasReduction(runId, BIAS_REDUCTION.COUNT)}
                      >
                        <div className={styles.innerButton}>
                          <img src={rSetup.biasReduction === BIAS_REDUCTION.COUNT ? BalanceIconP : BalanceIcon} />
                          {`${rSetup.transactionLabel} to ${rSetup.trueAction}`}
                        </div>
                      </Button>
                      <Button
                        className={styles.oButton}
                        active={rSetup.biasReduction === BIAS_REDUCTION.EFFORT}
                        onClick={() => updateBiasReduction(runId, BIAS_REDUCTION.EFFORT)}
                      >
                        <div className={styles.innerButton}>
                          <img src={rSetup.biasReduction === BIAS_REDUCTION.EFFORT ? SettingsIconP : SettingsIcon} />
                          {optimized.resourcingLabel}
                        </div>
                      </Button>
                      <Button
                        className={styles.oButton}
                        active={rSetup.biasReduction === BIAS_REDUCTION.IMPACT || rSetup.biasReduction === undefined}
                        onClick={() => updateBiasReduction(runId, BIAS_REDUCTION.IMPACT)}
                      >
                        <div className={styles.innerButton}>
                          <img
                            src={
                              rSetup.biasReduction === BIAS_REDUCTION.IMPACT || rSetup.biasReduction === undefined
                                ? BalanceIconP
                                : BalanceIcon
                            }
                          />
                          {optimized.profitLabel}
                        </div>
                      </Button>
                    </ButtonGroup>
                  </div>
                  <div style={{ marginTop: "20px" }} className={styles.oTitle}>
                    {"What is your representation target for each " + rSetup.splitColumn + "?"}
                  </div>
                  <div className={styles.segmentList}>
                    {subgroups.map((d, i) => (
                      <div className={styles.sItemO} key={i}>
                        <div className={styles.flexItem}>
                          <span
                            className={styles.eCircle}
                            style={{
                              background: d.color
                            }}
                          />
                          {d.label}
                        </div>
                        <LineInput
                          isMediumBox
                          topLabel={"Target"}
                          style={{ marginTop: "15px" }}
                          value={
                            d.setup.targetPositiveAllocation !== undefined
                              ? d.setup.targetPositiveAllocation + "%"
                              : d.pctOfRows === 0
                                ? "0"
                                : (d.pctOfRows * 100).toFixed(2) + "%"
                          }
                          onChange={(val) => {
                            const parsed = val === undefined ? val : Number(val.replace("%", ""));

                            const current =
                              d.setup.targetPositiveAllocation !== undefined
                                ? d.setup.targetPositiveAllocation
                                : d.pctOfRows === 0
                                  ? "0"
                                  : (d.pctOfRows * 100).toFixed(2);

                            if (Number(current) !== parsed) {
                              this.setState({ needsRecalculate: true });
                            }

                            updateTargetPositiveAllocation(d.runId, isNaN(parsed) ? 0 : parsed);
                          }}
                        />
                        <LineInput
                          isMediumBox
                          isDisabled
                          style={{ marginTop: "15px" }}
                          topLabel={"Achieved Value"}
                          value={
                            (
                              (splitChartData[splitOptionKey].find((x) => x.Subgroup == d.label) || {})[
                                (splitChartOptions.find((x) => x.key == splitOptionKey) || {}).label
                              ] || ""
                            ).replace(".00", "") + "%"
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: "20px", display: "flex" }}>
                    <div>
                      <Button
                        type="primary"
                        disabled={!this.state.needsRecalculate || Math.round(allocationTotal) !== 100}
                        onClick={() => {
                          this.setState({ needsRecalculate: false });
                          recalculateBudget();
                        }}
                      >
                        Calculate
                      </Button>
                    </div>
                    {Math.round(allocationTotal) !== 100 && (
                      <div className={styles.errors} style={{ marginTop: "0px", marginLeft: "10px" }}>
                        {`The values for each ${rSetup.splitColumn} must add up to 100. Current total: ${allocationTotal.toFixed(2)}`}
                      </div>
                    )}
                  </div>
                  {disclaimer}
                </div>
              )}
              {rSetup.fairnessObjective !== FAIRNESS_OBJECTIVE.ALLOCATION && (
                <div style={{ marginLeft: "20px" }} className={styles.bObjective}>
                  <div className={styles.oTitle}>
                    {`How close do two subgroups need to be for you to consider them roughly equal in terms of ${(splitChartOptions.find((x) => x.key == splitOptionKey) || {}).label}?`}
                  </div>
                  <div className={styles.oActions} style={{ display: "flex", marginTop: "10px" }}>
                    <div className={styles.sItemO}>
                      <LineInput
                        isMediumBox
                        topLabel={"Target Max Difference"}
                        value={
                          (rSetup.fairnessToleranceBound === undefined
                            ? DEFAULT_TOLERANCE_BOUND
                            : rSetup.fairnessToleranceBound) + "%"
                        }
                        onChange={(val) => {
                          const cleaned = val === undefined ? val : val.replace("%", "");

                          if (isFinite(cleaned)) {
                            updateFairnessToleranceBound(runId, Number(cleaned));
                          }
                        }}
                      />
                    </div>
                    <div className={styles.sItemO}>
                      <LineInput
                        isMediumBox
                        isDisabled
                        topLabel={"Achieved Max Difference"}
                        value={(Math.max(...values) - Math.min(...values)).toFixed(2) + "%"}
                      />
                    </div>
                    {Math.max(...values).toFixed(2) == "0.00" &&
                      (rSetup.fairnessToleranceBound === undefined || rSetup.fairnessToleranceBound > 0) && (
                        <div style={{ width: "50%" }}>
                          {`Aible selected ${rSetup.falseAction} for all ${rSetup.transactionLabel}. If this is not the desired result, please try increasing the Target Max Difference.`}
                        </div>
                      )}
                  </div>
                  {disclaimer}
                </div>
              )}
              <div className={styles.splitChart}>
                <div className={styles.splitChartSelect}>
                  <Select
                    options={splitChartOptions}
                    menuPlacement={"top"}
                    onChange={(val) => this.setState({ splitOption: val })}
                    value={splitOptionValue}
                  />
                </div>
                <SplitChart data={splitChartData} option={splitOptionValue} />
              </div>
            </div>
          )}
      </div>
    );
  }

  getLabelFromSegment(segment) {
    return segment === undefined || segment.conditions === undefined || segment.conditions.length === 0
      ? "Overall"
      : segment.conditions[0].value;
  }

  render() {
    const {
      approvedScenario,
      isContinuous,
      currentSpend,
      currentRevenue,
      currentProfit,
      setup,
      isEditBlueprint,
      optimized,
      runId,
      updateAdvancedCostLabel,
      updateAdvancedRevenueLabel
    } = this.props;

    const retIsContinuous = isContinuous || (optimized && optimized.underEstimate !== undefined);

    const unitType = setup.unitType !== undefined ? setup.unitType : "$";

    const isSkipped =
      approvedScenario &&
      optimized &&
      isSameSetup(approvedScenario.data, optimized.setup) &&
      setup &&
      setup.isFastTrain;

    return retIsContinuous ? (
      this.renderIsContinuous()
    ) : (
      <div
        className={styles.emptyOptimize}
        style={optimized && optimized.isCreating === false ? { paddingBottom: "200px" } : {}}
      >
        <div className={styles.emptyTop}>
          <ProjectHelp
            title={"What is your overall business objective?"}
            isDark
            onToggle={(isClosed) => this.setState({ isHelpClosed: isClosed })}
            noBot
          >
            <ProjectHelpItem isDark type={"targetGrey"} isLarge>
              {"Adding a business objective ensures that each scenario " +
                "created in the impact optimization step is " +
                "targeting your larger business objective. " +
                "This can be changed at any point."}
            </ProjectHelpItem>
          </ProjectHelp>
          <div className={styles.eBoxHolder}>
            {isEditBlueprint && false ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>
                  <LineInput
                    isMediumBox
                    isDark
                    value={optimized.resourcingLabel}
                    onChange={(val) => {
                      updateAdvancedCostLabel(runId, val);
                      updateAdvancedCostLabel("new", val);
                    }}
                  />
                </div>
                <div className={styles.eLightLarge}>{"TBD"}</div>
                <div>{"TBD"}</div>
              </div>
            ) : optimized && optimized.isCreating === false ? (
              <div
                className={styles.eBox}
                style={
                  setup.businessObjective === BUSINESS_OBJECTIVE.MINIMIZE_RESOURCING
                    ? {
                        background: "rgba(126, 105, 215, 0.5)"
                      }
                    : {}
                }
              >
                <div className={styles.eBoxPadding}>
                  <div className={styles.eDark}>{optimized.resourcingLabel}</div>
                  <div className={styles.eLightLarge}>
                    {optimized.isCurrencyUnitType ? (
                      <div>
                        {unitType}
                        {numeral(optimized.totalCost).format("0.0a")}
                      </div>
                    ) : (
                      <div className={styles.nonC}>
                        {numeral(optimized.totalCost).format("0.0a")}
                        <div className={styles.subLabel}>{unitType}</div>
                      </div>
                    )}
                  </div>
                  {isSkipped !== true ? (
                    <TinyBarChart
                      bars={[
                        {
                          label: "Optimal " + optimized.resourcingLabel,
                          Cost: optimized.totalCost
                        },
                        {
                          label: "Current " + optimized.resourcingLabel,
                          Cost: optimized.currentSpend
                        }
                      ]}
                      unit={optimized.unitLabel}
                      isCurrencyUnit={optimized.isCurrencyUnitType}
                    />
                  ) : (
                    <div>{"Add baseline assumptions to show difference"}</div>
                  )}
                </div>
              </div>
            ) : optimized && optimized.isCreating ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{optimized.resourcingLabel}</div>
                <div className={styles.eLoading}>
                  <LoadingIndicator style={{ margin: "0px" }} color={"#868595"} />
                </div>
                {this.renderProgress("Resourcing")}
              </div>
            ) : (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Baseline Resourcing"}</div>
                <div className={styles.eSubDark}>
                  {"This is your Business Assumptions Costs (the total cost " +
                    "per opportunity) times the Total Opportunities " +
                    "you expect to pursue."}
                </div>
                <div className={styles.eDarkLarge}>
                  {unitType === "$" ? (
                    <div>
                      {unitType}
                      {numeral(currentSpend).format("0.0a")}
                    </div>
                  ) : (
                    <div className={styles.nonC}>
                      {numeral(currentSpend).format("0.0a")}
                      <div className={styles.subLabel}>{unitType}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {isEditBlueprint ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Recommended Mix"}</div>
              </div>
            ) : optimized && optimized.isCreating === false ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Recommended Mix"}</div>
                {this.renderMix()}
              </div>
            ) : optimized && optimized.isCreating ? (
              <div className={styles.eBox} style={{ justifyContent: "flex-start" }}>
                <div className={styles.eDark}>{"Recommended Mix"}</div>
                <div className={styles.eLoading}>
                  <LoadingIndicator
                    style={{
                      margin: "0px",
                      marginTop: "10px"
                    }}
                    color={"#868595"}
                  />
                </div>
              </div>
            ) : (
              <div
                className={styles.eBox}
                style={{
                  justifyContent: "flex-start",
                  whiteSpace: "wrap"
                }}
              >
                <div className={styles.eDark}>{"Recommended Mix"}</div>
                <div className={styles.eSubDark}>
                  {"Aible will show the optimal resource allocation once the AI has been trained."}
                </div>
              </div>
            )}
            {isEditBlueprint && false ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>
                  <LineInput
                    isMediumBox
                    isDark
                    value={optimized.revenueLabel}
                    onChange={(val) => {
                      updateAdvancedRevenueLabel(runId, val);
                      updateAdvancedRevenueLabel("new", val);
                    }}
                  />
                </div>
                <div className={styles.eLightLarge}>{"TBD"}</div>

                <div>{"TBD"}</div>
              </div>
            ) : optimized && optimized.isCreating === false ? (
              <div
                className={styles.eBox}
                style={
                  setup.businessObjective === BUSINESS_OBJECTIVE.REVENUE
                    ? {
                        background: "rgba(126, 105, 215, 0.5)"
                      }
                    : {}
                }
              >
                <div className={styles.eBoxPadding}>
                  <div className={styles.eDark}>{optimized.revenueLabel}</div>
                  <div className={styles.eLightLarge}>
                    {optimized.isCurrencyUnitType ? (
                      <div>
                        {unitType}
                        {numeral(optimized.revenue).format("0.0a")}
                      </div>
                    ) : (
                      <div className={styles.nonC}>
                        {numeral(optimized.revenue).format("0.0a")}
                        <div className={styles.subLabel}>{unitType}</div>
                      </div>
                    )}
                  </div>
                  {isSkipped !== true ? (
                    <TinyBarChart
                      bars={[
                        {
                          label: "Optimal " + optimized.revenueLabel,
                          Cost: optimized.revenue
                        },
                        {
                          label: "Current " + optimized.revenueLabel,
                          Cost: optimized.currentRevenue
                        }
                      ]}
                      unit={optimized.unitLabel}
                      isCurrencyUnit={optimized.isCurrencyUnitType}
                    />
                  ) : (
                    <div>{"Add baseline assumptions to show difference"}</div>
                  )}
                </div>
              </div>
            ) : optimized && optimized.isCreating ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{optimized.revenueLabel}</div>
                <div className={styles.eLoading}>
                  <LoadingIndicator style={{ margin: "0px" }} color={"#868595"} />
                </div>
                {this.renderProgress(optimized.revenueLabel)}
              </div>
            ) : (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Baseline Revenue"}</div>
                <div className={styles.eSubDark}>
                  {"This uses your Business Assumption Benefits (expected revenue " +
                    "per opportunitiy) times your Total Expected Win Count."}
                </div>
                <div className={styles.eDarkLarge}>
                  {unitType === "$" ? (
                    <div>
                      {unitType}
                      {numeral(currentRevenue).format("0.0a")}
                    </div>
                  ) : (
                    <div className={styles.nonC}>
                      {numeral(currentRevenue).format("0.0a")}
                      <div className={styles.subLabel}>{unitType}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {isEditBlueprint && false ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>
                  <LineInput
                    isMediumBox
                    isDark
                    value={optimized.profitLabel}
                    onChange={(val) => {
                      updateNetImpactLabel(runId, val);
                      updateNetImpactLabel("new", val);
                    }}
                  />
                </div>
                <div className={styles.eLightLarge}>{"TBD"}</div>
                <div>{"TBD"}</div>
              </div>
            ) : optimized && optimized.isCreating === false ? (
              <div
                className={styles.eBox}
                style={
                  setup.businessObjective === BUSINESS_OBJECTIVE.PROFIT || setup.businessObjective === undefined
                    ? {
                        background: "rgba(126, 105, 215, 0.5)"
                      }
                    : {}
                }
              >
                <div className={styles.eBoxPadding}>
                  <div className={styles.eDark}>{optimized.profitLabel}</div>
                  <div className={styles.eLightLarge}>
                    {optimized.isCurrencyUnitType ? (
                      <div>
                        {unitType}
                        {numeral(optimized.profit).format("0.0a")}
                      </div>
                    ) : (
                      <div className={styles.nonC}>
                        {numeral(optimized.profit).format("0.0a")}
                        <div className={styles.subLabel}>{unitType}</div>
                      </div>
                    )}
                  </div>
                  {isSkipped !== true ? (
                    <TinyBarChart
                      bars={[
                        {
                          label: "Optimal " + optimized.profitLabel,
                          Cost: optimized.profit
                        },
                        {
                          label: "Current " + optimized.profitLabel,
                          Cost: optimized.currentProfit
                        }
                      ]}
                      unit={optimized.unitLabel}
                      isCurrencyUnit={optimized.isCurrencyUnitType}
                    />
                  ) : (
                    <div>{"Add baseline assumptions to show difference"}</div>
                  )}
                </div>
              </div>
            ) : optimized && optimized.isCreating ? (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{optimized.profitLabel}</div>
                <div className={styles.eLoading}>
                  <LoadingIndicator style={{ margin: "0px" }} color={"#868595"} />
                </div>
                {this.renderProgress(optimized.profitLabel)}
              </div>
            ) : (
              <div className={styles.eBox}>
                <div className={styles.eDark}>{"Baseline Profit"}</div>
                <div className={styles.eSubDark}>
                  {"This is the sum of the unique businesss circumstances you defined" +
                    " in the Costs/Benefits and Expected Win Count across all" +
                    " potential opportunities."}
                </div>
                <div className={styles.eDarkLarge}>
                  {unitType === "$" ? (
                    <div>
                      {unitType}
                      {numeral(currentProfit).format("0.0a")}
                    </div>
                  ) : (
                    <div className={styles.nonC}>
                      {numeral(currentProfit).format("0.0a")}
                      <div className={styles.subLabel}>{unitType}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.emptyBot}>
          <div className={styles.eBotBody}>{this.renderObjectives()}</div>
        </div>
      </div>
    );
  }
}
